import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnchorContext, type AnchorId } from '../../lib/tour/anchors.js';
import { isGuideSurface } from '../../lib/tour/steps.js';
import { useTour } from '../../lib/tour/TourProvider.js';

/**
 * The guide, drawn over the live console.
 *
 * The house lights go down and a followspot travels to whatever the step is
 * about — the same backstage/on-stage split the rest of the design uses, with
 * the scrim in `--stage` because dimming the room is exactly what it means. The
 * step counter is a pair of split-flap cells that re-flap on every step, tying
 * the guide to the join code, which is the product's signature object.
 *
 * **There is no focus trap, and that is the point.** The usual coachmark is
 * `aria-modal` with focus held inside it, which would put the highlighted
 * control out of reach and make an "interactive" guide a slideshow. So the
 * scrim is `pointer-events: none` in every part — the dim is guidance, not a
 * cage — the card is a non-modal `dialog`, and Tab from the card lands on the
 * control the step is describing. Escape leaves.
 *
 * The card takes focus on each step so the change is announced; the counter is
 * decorative and carries its meaning in visually-hidden text instead.
 */

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Breathing room between the highlighted element and the edge of the light. */
const HALO = 8;
const GAP = 14;
const CARD_WIDTH = 336;
const EDGE = 16;
/** Below the anchor unless the card would be cramped there. */
const ROOM_FOR_CARD = 210;

const sameRect = (a: Rect | null, b: Rect) =>
  a !== null &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Stable no-op fallbacks: useSyncExternalStore resubscribes whenever `subscribe`
// changes identity, so these cannot be inline arrow functions.
const noSubscribe = () => () => {};
const noVersion = () => 0;

/**
 * Live viewport rect of an anchor and the element itself, or nulls when it is
 * not mounted.
 *
 * Re-measures on scroll, resize, and any change to the anchor registry, so the
 * spotlight keeps up with a page that reflows underneath it — adding a screen
 * in the builder moves everything below it.
 */
function useAnchor(id: AnchorId | undefined): { rect: Rect | null; element: HTMLElement | null } {
  const store = useContext(AnchorContext);
  const version = useSyncExternalStore(
    store?.subscribe ?? noSubscribe,
    store?.getVersion ?? noVersion,
    noVersion,
  );
  const [rect, setRect] = useState<Rect | null>(null);
  const element = id && store ? store.get(id) : null;

  useEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const next = element.getBoundingClientRect();
      const value = { top: next.top, left: next.left, width: next.width, height: next.height };
      setRect((previous) => (sameRect(previous, value) ? previous : value));
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    measure();
    // Capture phase: the console scrolls the window, but a scrollable panel
    // between us and it would not bubble.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(element);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
    // `version` is a dependency because a remount swaps the element behind the id.
  }, [element, version]);

  return { rect, element };
}

function CardCounter({ position }: { position: number }) {
  const digits = String(position).padStart(2, '0').split('');
  return (
    <span className="tour-counter" aria-hidden="true">
      {digits.map((digit, index) => (
        // Keyed by value so a changed digit remounts and re-flaps, while a digit
        // that did not change stays still — a real split-flap only turns what moved.
        <span key={`${index}-${digit}`} className="code-cell tour-counter-cell">
          {digit}
        </span>
      ))}
    </span>
  );
}

export function TourOverlay() {
  const { phase, step, position, total, next, back, skip } = useTour();
  const { pathname } = useLocation();
  const { rect, element } = useAnchor(step?.anchor);
  const cardRef = useRef<HTMLDivElement>(null);
  const [still] = useState(prefersReducedMotion);
  const hidden = phase === 'off' || !isGuideSurface(pathname);

  // Escape is global rather than on the card: the step often asks the user to
  // type into the highlighted field, and leaving must work from there too.
  useEffect(() => {
    if (hidden) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') skip();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hidden, skip]);

  // Bring the anchor into view before the light lands on it. Keyed on the step
  // and the anchor node, never on the measured rect — depending on the rect
  // would re-centre the page on every scroll event and take the scrollbar away
  // from the user.
  useEffect(() => {
    if (hidden || phase !== 'running' || !element) return;
    element.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
  }, [hidden, phase, step?.id, still, element]);

  useLayoutEffect(() => {
    if (hidden || phase !== 'running') return;
    cardRef.current?.focus({ preventScroll: true });
  }, [hidden, phase, step]);

  if (hidden) return null;

  if (phase === 'standing-by') {
    return createPortal(
      <div className="tour-dock" role="status">
        <div>
          <p className="eyebrow">Guide paused</p>
          <p className="tour-dock-line">
            {step?.route === 'builder'
              ? 'Open a poll and the guide picks up in the builder.'
              : 'Head back to your polls and the guide picks up there.'}
          </p>
        </div>
        <button type="button" className="secondary" onClick={skip}>
          Leave guide
        </button>
      </div>,
      document.body,
    );
  }

  if (phase !== 'running' || !step) return null;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(CARD_WIDTH, viewportWidth - EDGE * 2);

  // A step with no anchor, or one whose element has not mounted, becomes a
  // centered card rather than a spotlight on nothing.
  const spot = rect
    ? {
        top: rect.top - HALO,
        left: rect.left - HALO,
        width: rect.width + HALO * 2,
        height: rect.height + HALO * 2,
      }
    : null;

  const below = spot ? viewportHeight - (spot.top + spot.height) : 0;
  const placeBelow = spot ? below >= ROOM_FOR_CARD || below >= spot.top : true;

  const cardStyle: CSSProperties = spot
    ? {
        width,
        left: Math.min(
          Math.max(EDGE, spot.left + spot.width / 2 - width / 2),
          Math.max(EDGE, viewportWidth - width - EDGE),
        ),
        top: placeBelow ? spot.top + spot.height + GAP : spot.top - GAP,
        transform: placeBelow ? undefined : 'translateY(-100%)',
      }
    : { width, left: (viewportWidth - width) / 2, top: '50%', transform: 'translateY(-50%)' };

  const titleId = `tour-title-${step.id}`;
  const bodyId = `tour-body-${step.id}`;

  return createPortal(
    <div className={`tour${still ? ' tour-still' : ''}`}>
      {spot ? (
        <>
          {/* Four panels leaving a real gap, not a cut-out illusion: the
              highlighted control is genuinely uncovered, so nothing intercepts
              a click meant for it. */}
          <div className="tour-scrim" style={{ top: 0, left: 0, right: 0, height: spot.top }} />
          <div
            className="tour-scrim"
            style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0 }}
          />
          <div
            className="tour-scrim"
            style={{ top: spot.top, left: 0, width: spot.left, height: spot.height }}
          />
          <div
            className="tour-scrim"
            style={{
              top: spot.top,
              left: spot.left + spot.width,
              right: 0,
              height: spot.height,
            }}
          />
          <div className="tour-ring" style={spot} />
        </>
      ) : (
        <div className="tour-scrim" style={{ inset: 0 }} />
      )}

      <div
        className="tour-card"
        style={cardStyle}
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') next();
          if (event.key === 'ArrowLeft') back();
        }}
      >
        <div className="tour-card-head">
          <CardCounter position={position} />
          <p className="eyebrow">of {total}</p>
          <span className="visually-hidden">
            Step {position} of {total}
          </span>
        </div>

        <h2 id={titleId}>{step.title}</h2>
        <p id={bodyId}>{step.body}</p>
        {step.note ? <p className="tour-card-note">{step.note}</p> : null}

        <div className="tour-card-actions">
          <button type="button" className="tour-skip" onClick={skip}>
            Leave guide
          </button>
          <div className="tour-card-steps">
            {position > 1 ? (
              <button type="button" className="secondary" onClick={back}>
                Back
              </button>
            ) : null}
            <button type="button" onClick={next}>
              {position === total ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
